import { useContext } from "react";
import { createFailure, validationFailed, ValidationResult, validationSuccessful } from "sprint-common-web";

import { LanguageListingContext } from "../context/LanguageListingContext";
import { LanguageItem } from "../listing/LanguagesListing";
import { useAdminLanguageApi } from "../useAdminLanguageApi";
import { CreateLanguageFormInputs } from "./CreateLanguageForm";

export const useCreateLanguageForm = () => {
    const { createLanguage } = useAdminLanguageApi();
    const { languageItems, addLanguageItem } = useContext(LanguageListingContext);

    const validateCode = (code: string | undefined): ValidationResult => {
        if (!code) {
            return validationSuccessful();
        }

        const usedLanguageCodes = languageItems.map((languageItem: LanguageItem) => languageItem.code.toLowerCase());

        if (usedLanguageCodes.includes(code.toLowerCase())) {
            const failure = createFailure("This code is already taken", "language.CodeAlreadyTaken");

            return validationFailed([failure]);
        }

        return validationSuccessful();
    };

    const validateBuzzCode = (buzzCode: string): ValidationResult => {
        const usedBuzzCodes = languageItems.flatMap((languageItem: LanguageItem) => {
            return [
                ...languageItem.buzzCodes.audio,
                ...languageItem.buzzCodes.dynamicSubtitles,
                ...languageItem.buzzCodes.embeddedSubtitles
            ].map((item: string) => item.toLowerCase());
        });

        if (usedBuzzCodes.includes(buzzCode.toLowerCase())) {
            const failure = createFailure("This buzz code is already taken", "language.BuzzCodeAlreadyTaken");

            return validationFailed([failure]);
        }

        return validationSuccessful();
    };

    const validateSubtitleBuzzCode = (
        buzzCode: string | undefined,
        { buzzDynamicSubtitlesCodes, buzzEmbeddedSubtitlesCodes }: CreateLanguageFormInputs
    ): ValidationResult => {
        if (!buzzCode) {
            return validationSuccessful();
        }

        const currSubtitleBuzzCodes = [...buzzDynamicSubtitlesCodes, ...buzzEmbeddedSubtitlesCodes]
            .filter((item) => !!item.value)
            .map((item) => item.value.toLowerCase());
        const duplicatedSubs = toFindDuplicates(currSubtitleBuzzCodes);

        if (duplicatedSubs.includes(buzzCode.toLowerCase())) {
            const failure = createFailure("This buzz code is duplicated", "language.DuplicatedSubtitleBuzzCode");

            return validationFailed([failure]);
        }

        return validateBuzzCode(buzzCode);
    };

    const validateAudioBuzzCode = (
        buzzCode: string | undefined,
        { buzzAudioCodes }: CreateLanguageFormInputs
    ): ValidationResult => {
        if (!buzzCode) {
            return validationSuccessful();
        }

        const duplicatedAudios = toFindDuplicates(buzzAudioCodes.map((item) => item.value.toLowerCase()));

        if (duplicatedAudios.includes(buzzCode.toLowerCase())) {
            const failure = createFailure("This buzz code is duplicated", "language.DuplicatedSubtitleBuzzCode");

            return validationFailed([failure]);
        }

        return validateBuzzCode(buzzCode);
    };

    const validateShreqCode = (shreqCode: string): ValidationResult => {
        const usedShreqCodes = languageItems.flatMap((languageItem: LanguageItem) => {
            return [
                ...languageItem.shreqCodes.audio,
                ...languageItem.shreqCodes.dynamicSubtitles,
                ...languageItem.shreqCodes.embeddedSubtitles
            ].map((item: string) => item.toLowerCase());
        });

        if (usedShreqCodes.includes(shreqCode.toLowerCase())) {
            const failure = createFailure("This shreq code is already taken", "language.ShreqCodeAlreadyTaken");

            return validationFailed([failure]);
        }

        return validationSuccessful();
    };

    const validateSubtitleShreqCode = (
        shreqCode: string | undefined,
        { shreqDynamicSubtitlesCodes, shreqEmbeddedSubtitlesCodes }: CreateLanguageFormInputs
    ): ValidationResult => {
        if (!shreqCode) {
            return validationSuccessful();
        }

        const currSubtitleShreqCodes = [...shreqDynamicSubtitlesCodes, ...shreqEmbeddedSubtitlesCodes]
            .filter((item) => !!item.value)
            .map((item) => item.value.toLowerCase());
        const duplicatedSubs = toFindDuplicates(currSubtitleShreqCodes);

        if (duplicatedSubs.includes(shreqCode.toLowerCase())) {
            const failure = createFailure("This shreq code is duplicated", "language.DuplicatedSubtitleShreqCode");

            return validationFailed([failure]);
        }

        return validateShreqCode(shreqCode);
    };

    const validateAudioShreqCode = (
        shreqCode: string | undefined,
        { shreqAudioCodes }: CreateLanguageFormInputs
    ): ValidationResult => {
        if (!shreqCode) {
            return validationSuccessful();
        }

        const duplicatedAudios = toFindDuplicates(shreqAudioCodes.map((item) => item.value.toLowerCase()));

        if (duplicatedAudios.includes(shreqCode.toLowerCase())) {
            const failure = createFailure("This shreq code is duplicated", "language.DuplicatedSubtitleShreqCode");

            return validationFailed([failure]);
        }

        return validateShreqCode(shreqCode);
    };

    const validateLanguageType = ({ isDubbing, isSubtitle }: CreateLanguageFormInputs) => {
        if (!isDubbing && !isSubtitle) {
            const failure = createFailure(
                "Select at least one from isSubtitle or isDubbing",
                "language.UnkownLanguageType"
            );

            return validationFailed([failure]);
        }

        return validationSuccessful();
    };

    const addLanguage = async ({
        name,
        code,
        iso639,
        laRg,
        isSubtitle,
        isDubbing,
        isCaption,
        buzzAudioCodes,
        buzzDynamicSubtitlesCodes,
        buzzEmbeddedSubtitlesCodes,
        shreqAudioCodes,
        shreqDynamicSubtitlesCodes,
        shreqEmbeddedSubtitlesCodes
    }: CreateLanguageFormInputs): Promise<void> => {
        const uniqueBuzzAudioCodeValues = Array.from(
            new Set(buzzAudioCodes.map((buzzCode) => buzzCode.value).filter((value) => value))
        );
        const uniqueBuzzDynamicSubtitleCodeValues = Array.from(
            new Set(buzzDynamicSubtitlesCodes.map((buzzCode) => buzzCode.value).filter((value) => value))
        );
        const uniqueBuzzEmbeddedSubtitleCodeValues = Array.from(
            new Set(buzzEmbeddedSubtitlesCodes.map((buzzCode) => buzzCode.value).filter((value) => value))
        );

        const uniqueShreqAudioCodeValues = Array.from(
            new Set(shreqAudioCodes.map((shreqCode) => shreqCode.value).filter((value) => value))
        );
        const uniqueShreqDynamicSubtitleCodeValues = Array.from(
            new Set(shreqDynamicSubtitlesCodes.map((shreqCode) => shreqCode.value).filter((value) => value))
        );
        const uniqueShreqEmbeddedSubtitleCodeValues = Array.from(
            new Set(shreqEmbeddedSubtitlesCodes.map((shreqCode) => shreqCode.value).filter((value) => value))
        );

        const response = await createLanguage({
            name,
            code,
            iso639,
            laRg,
            isSubtitle,
            isDubbing,
            isCaption,
            buzzAudioCodes: uniqueBuzzAudioCodeValues,
            buzzDynamicSubtitleCodes: uniqueBuzzDynamicSubtitleCodeValues,
            buzzEmbeddedSubtitleCodes: uniqueBuzzEmbeddedSubtitleCodeValues,
            shreqAudioCodes: uniqueShreqAudioCodeValues,
            shreqDynamicSubtitleCodes: uniqueShreqDynamicSubtitleCodeValues,
            shreqEmbeddedSubtitleCodes: uniqueShreqEmbeddedSubtitleCodeValues
        });

        const newLanguageItem: LanguageItem = {
            id: response.entityId,
            name,
            code,
            iso639,
            laRg,
            isSubtitle,
            isDubbing,
            isCaption,
            buzzCodes: {
                audio: uniqueBuzzAudioCodeValues,
                dynamicSubtitles: uniqueBuzzDynamicSubtitleCodeValues,
                embeddedSubtitles: uniqueBuzzEmbeddedSubtitleCodeValues
            },
            shreqCodes: {
                audio: uniqueShreqAudioCodeValues,
                dynamicSubtitles: uniqueShreqDynamicSubtitleCodeValues,
                embeddedSubtitles: uniqueShreqEmbeddedSubtitleCodeValues
            },
            isModified: true
        };

        addLanguageItem(newLanguageItem);
    };

    return {
        validateLanguageType,
        addLanguage,
        validateCode,
        validateAudioBuzzCode,
        validateSubtitleBuzzCode,
        validateAudioShreqCode,
        validateSubtitleShreqCode
    };
};

const toFindDuplicates = (arry: string[]) => arry.filter((item, index) => arry.indexOf(item) !== index);
